import { useState, type ReactNode } from "react";
import { Button, Card, Chip, Tabs, buttonVariants } from "@heroui/react";
import {
  ArrowRight,
  Box,
  FileDown,
  FileText,
  Frame,
  Layers,
  Library,
  Table2,
  Users,
  type LucideIcon,
} from "lucide-react";
import { Modal } from "./Modal";
import { BookDemoButton, MarketingHeader } from "./MarketingHeader";
import { LandingWorkspace } from "./LandingWorkspace";
import { useDeskScrollGuard } from "./desk-scroll-guard";
import "./welcome.css";
import "./workspace-access.css";
import "./product-landing.css";

/** Callers render their own access controls; the page decides the size. */
export type LandingAction = (size: "sm" | "md" | "lg") => ReactNode;
export const landingButtonClass = (
  size: "sm" | "md" | "lg",
  variant: "primary" | "ghost" | "outline" = "primary",
) => buttonVariants({ size, variant });

/** Three review essentials, each shown with the real interface. */
const review = [
  {
    src: "/landing/six/comment.webp",
    width: 1680,
    height: 1120,
    alt: "A comment pinned to the courtyard opening on the plan: Emma asks whether the opening can be widened, Marc replies that Option B widens it and keeps the layout.",
    title: "Comments on the drawing",
    copy: "Pinned to the wall or opening they concern. Clients and consultants join to view, comment or edit.",
  },
  {
    src: "/landing/six/calibrate.webp",
    width: 880,
    height: 844,
    alt: "The Set scale dialog: enter the known distance between two points picked on the plan, 2.4 metres, then apply the scale.",
    title: "Measure at scale",
    copy: "Calibrate a PDF, DXF or scan on one known dimension. Until then it is marked Not to scale.",
  },
  {
    src: "/landing/six/picks.webp",
    width: 1640,
    height: 600,
    alt: "Two option images side by side, each with a Pick checkbox; the second, the renovated kitchen, is picked.",
    title: "Let the client pick",
    copy: "Put options side by side and let reviewers pick. The agreed set exports as one PDF.",
  },
] as const;

const alsoOnTheDesk: { label: string; icon: LucideIcon }[] = [
  { label: "Sheets from A4 to A0", icon: Frame },
  { label: "Tracing paper", icon: Layers },
  { label: "Draw together, live", icon: Users },
  { label: "Tables and schedules", icon: Table2 },
  { label: "Briefs beside the sheets", icon: FileText },
  { label: "3D model viewer", icon: Box },
  { label: "Library across projects", icon: Library },
  { label: "One PDF for the review set", icon: FileDown },
];

const formats = [
  "PDF",
  "DXF",
  "PNG",
  "JPEG",
  "WebP",
  "DOCX",
  "XLSX",
  "CSV",
  "GLB",
  "OBJ",
  "FBX",
] as const;

/** Real runs of the agent panel on the synthetic courtyard example. */
const agentCases = [
  {
    id: "make-3d",
    label: "Make 3D",
    request: "Make 3D from this drawing.",
    result:
      "One click on a plan. The agent reads the drawing, builds a scaled model and places it beside the sheet.",
    src: "/landing/agent/make-3d.webp",
    alt: "The courtyard plan on the left, a 3D model of the house beside it, and the Agent panel on the right: asked to make 3D from the drawing, it built an approximate model with 3 m walls, flat roofs and an open planted courtyard, and offers Undo.",
  },
  {
    id: "edit-model",
    label: "Change the model",
    request:
      "Make the outer walls 4.5 m tall and give the bedroom wing a gable roof.",
    result:
      "Follow up in plain words. Only the walls and roof that change rebuild, and Undo takes them back.",
    src: "/landing/agent/edit-model.webp",
    alt: "The same 3D model with taller outer walls and a gable roof over the bedroom wing; the Agent panel says it updated the model and lists Updated 3D model with Undo.",
  },
  {
    id: "schedule",
    label: "Draw up a sheet",
    request:
      "Add an A3 landscape sheet to the right of the plan called Finishes, with a table of rooms and floor finishes: Bedroom – oak boards, Studio – oak boards, Living – oak boards, Kitchen / dining – terrazzo, Courtyard – limestone pavers.",
    result:
      "Sheets, tables and notes arrive drawn on the canvas, each listed in the reply so you can jump to it.",
    src: "/landing/agent/schedule.webp",
    alt: "A new A3 sheet titled Finishes with an editable table of five rooms and their floor finishes; the Agent panel lists Added sheet, Added text and Added table, with Undo.",
  },
] as const;

const reducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** The plan becoming the room, recorded from the study's own renderer. */
function PlanToSpace() {
  const [still] = useState(reducedMotion);
  const poster = "/landing/six/plan-to-space-poster.webp";
  const alt =
    "The courtyard apartment plan rising into a schematic 3D model: floors, walls with openings and the main furniture, seen from above at an angle.";
  return (
    <Card className="product-media-card">
      <figure>
        {still ? (
          <img
            src={poster}
            alt={alt}
            width={1440}
            height={810}
            loading="lazy"
            decoding="async"
            className="w-full"
          />
        ) : (
          <video
            poster={poster}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            width={1440}
            height={810}
            aria-label={alt}
            className="w-full"
          >
            <source src="/landing/six/plan-to-space.webm" type="video/webm" />
            <source src="/landing/six/plan-to-space.mp4" type="video/mp4" />
          </video>
        )}
        <figcaption className="product-media-caption">
          Schematic study with stated assumptions, not a calibrated model.
        </figcaption>
      </figure>
    </Card>
  );
}

/** The live demo; it only takes the wheel and swipes once clicked. */
function Desk() {
  const { ref, engaged, hint } = useDeskScrollGuard();
  return (
    <div className="product-desk">
      <div
        ref={ref}
        className="landing-desk product-desk-frame"
        data-engaged={engaged}
      >
        <LandingWorkspace />
        <p
          className="landing-desk-hint pointer-events-none absolute bottom-20 left-1/2 z-[400] -translate-x-1/2 rounded-full bg-foreground/85 px-4 py-2 text-sm whitespace-nowrap text-background backdrop-blur"
          data-visible={hint}
          aria-hidden="true"
        >
          Click the desk to pan and zoom
        </p>
      </div>
      <p className="product-desk-caption" aria-live="polite">
        <span>
          Courtyard apartment · draw, measure and comment; nothing is saved.
        </span>
        <span className="font-mono text-xs">
          {engaged
            ? "Desk active · click outside to scroll the page"
            : "Click or tap the desk to use it"}
        </span>
      </p>
    </div>
  );
}

/** The agent panel, shown through three real requests. */
function AgentShowcase() {
  return (
    <section
      id="agent"
      className="product-section marketing-wrap"
      aria-labelledby="entry-agent-title"
    >
      <div className="product-section-head">
        <div className="product-heading-with-status">
          <h2 id="entry-agent-title">
            An agent beside
            <br />
            the drawing.
          </h2>
          <Chip size="sm" variant="soft">
            Early access
          </Chip>
        </div>
        <p>
          Point to a sheet and say what you need. It works on the canvas, only
          when you ask, with Undo for every change.
        </p>
      </div>
      <Tabs
        defaultSelectedKey={agentCases[0].id}
        className="product-agent-tabs"
      >
        <Tabs.ListContainer className="product-agent-list">
          <Tabs.List aria-label="Agent examples">
            {agentCases.map((item) => (
              <Tabs.Tab key={item.id} id={item.id}>
                {item.label}
                <Tabs.Indicator />
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs.ListContainer>
        {agentCases.map((current) => (
          <Tabs.Panel key={current.id} id={current.id}>
            <Card className="product-media-card">
              <img
                key={current.id}
                src={current.src}
                alt={current.alt}
                width={1800}
                height={1125}
                loading="lazy"
                decoding="async"
              />
              <Card.Footer className="product-agent-caption">
                <p>“{current.request}”</p>
                <p>{current.result}</p>
              </Card.Footer>
            </Card>
          </Tabs.Panel>
        ))}
      </Tabs>
    </section>
  );
}

/** The presentation is shared; each environment retains its own access flow. */
export function Welcome({
  children,
  startAction,
  signInAction,
  accessNote,
  notice,
  accessOnly = false,
}: {
  children?: ReactNode;
  startAction?: LandingAction;
  signInAction?: ReactNode;
  accessNote?: ReactNode;
  notice?: ReactNode;
  accessOnly?: boolean;
}) {
  const [accessOpen, setAccessOpen] = useState(false);
  const start: LandingAction =
    startAction ??
    ((size) => (
      <Button size={size} onPress={() => setAccessOpen(true)}>
        Start a project
      </Button>
    ));
  const signIn = signInAction ?? (
    <Button size="sm" variant="ghost" onPress={() => setAccessOpen(true)}>
      Sign in
    </Button>
  );
  if (accessOnly)
    return (
      <main className="calque-landing workspace-access">
        <a href="/" className="workspace-access-brand" aria-label="Calque home">
          calque
        </a>
        <div className="workspace-access-panel">
          <h1>Open your workspace.</h1>
          {notice}
          {children ?? (
            <div className="workspace-access-actions">
              {signIn}
              {start("md")}
            </div>
          )}
          {accessNote && <p>{accessNote}</p>}
          <a href="/product">Explore the product</a>
        </div>
      </main>
    );
  return (
    <div className="studio-entry calque-landing calque-marketing product-page">
      <a className="marketing-skip" href="#entry-main">
        Skip to content
      </a>
      <MarketingHeader product actions={signIn} />
      <main id="entry-main">
        <section
          className="product-hero marketing-wrap"
          aria-labelledby="entry-title"
        >
          <div className="product-identity">
            <span>Calque Workspace</span>
            <Chip size="sm" variant="soft">
              Early access
            </Chip>
          </div>
          <div className="product-hero-grid">
            <h1 id="entry-title">
              Review drawings,
              <br />
              together.
            </h1>
            <div className="product-hero-description">
              <p>
                Bring in plans and references, measure at scale and review with
                your team and clients.
              </p>
              <div className="product-hero-actions">
                {start("md")}
                <BookDemoButton variant="outline" />
              </div>
              {notice && (
                <div className="product-notice" role="status">
                  {notice}
                </div>
              )}
            </div>
          </div>
        </section>

        <section
          id="workspace-demo"
          className="product-demo"
          aria-label="Try the Calque workspace"
        >
          <div className="marketing-wrap">
            <Desk />
          </div>
        </section>

        <section
          id="review"
          className="product-section marketing-wrap"
          aria-labelledby="entry-review-title"
        >
          <div className="product-section-head">
            <h2 id="entry-review-title">
              Keep the review
              <br />
              on the drawing.
            </h2>
          </div>
          <ul className="product-review-grid">
            {review.map((item) => (
              <li key={item.title}>
                <Card variant="transparent" className="product-review-card">
                  <div className="product-review-image">
                    <img
                      src={item.src}
                      alt={item.alt}
                      width={item.width}
                      height={item.height}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <Card.Header>
                    <Card.Title>{item.title}</Card.Title>
                    <Card.Description>{item.copy}</Card.Description>
                  </Card.Header>
                </Card>
              </li>
            ))}
          </ul>
        </section>

        <section
          id="floorplan-to-3d"
          className="product-section product-section-ruled marketing-wrap"
          aria-labelledby="entry-space-title"
        >
          <div className="product-section-head">
            <h2 id="entry-space-title">
              Select a plan.
              <br />
              Explore the space.
            </h2>
            <p>
              Build a schematic 3D study from a plan, orbit and cut it, and put
              the view on a sheet.
            </p>
          </div>
          <PlanToSpace />
        </section>

        <div className="product-agent-band">
          <AgentShowcase />
        </div>

        <section
          className="product-included marketing-wrap"
          aria-labelledby="entry-included-title"
        >
          <div>
            <h3 id="entry-included-title">Also in the workspace</h3>
            <ul className="product-feature-list">
              {alsoOnTheDesk.map(({ label, icon: Icon }) => (
                <li key={label}>
                  <Icon size={16} aria-hidden="true" />
                  <span>{label}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3>Works with</h3>
            <div className="product-formats">
              {formats.map((format) => (
                <Chip key={format} size="sm" variant="soft">
                  {format}
                </Chip>
              ))}
            </div>
          </div>
        </section>

        <section
          className="product-bridge"
          aria-labelledby="entry-bridge-title"
        >
          <img
            src="/landing/vision/construction-hero-v2.webp"
            srcSet="/landing/vision/construction-hero-v2-840.webp 840w, /landing/vision/construction-hero-v2.webp 1672w"
            sizes="100vw"
            width={1672}
            height={941}
            loading="lazy"
            decoding="async"
            alt=""
          />
          <div className="product-bridge-content marketing-wrap">
            <h2 id="entry-bridge-title">
              The shared record
              <br />
              starts with the drawing.
            </h2>
            <div>
              <p>
                We’re building it out to connect architects, engineers and
                contractors.
              </p>
              <div className="product-hero-actions">
                {start("md")}
                <a href="/" className="product-bridge-link">
                  See the vision <ArrowRight size={15} aria-hidden="true" />
                </a>
              </div>
              {accessNote && (
                <p className="product-access-note">{accessNote}</p>
              )}
            </div>
          </div>
        </section>
      </main>

      {accessOpen && (
        <div className="entry-access">
          <Modal
            title="Open your workspace"
            close={() => setAccessOpen(false)}
            initialFocus="input"
          >
            {children}
          </Modal>
        </div>
      )}
    </div>
  );
}
